<template>
    <div
        ref="voiceLevelContainer"
        :style="variant === 'dots' ? undefined : `gap: ${BAR_GAP}px`"
        :class="variant === 'dots' ? 'h-5 justify-between' : ['h-auto', sizeStyle.container]"
        class="overflow-y-clip origin-bottom flex items-center mx-auto">
        <template v-for="(level, idx) in levelHistorySlice" :key="idx">
            <div v-if="variant === 'dots'" :class="dotBarClasses(level)" class="shrink-0 rounded-full transition-[height,background-color] duration-150 ease-linear"></div>
            <div
                v-else
                :style="[levelHeight(level), `width: ${BAR_WIDTH}px`]"
                :class="sizeStyle.bar"
                class="transition-transform duration-200 ease-linear rounded-full bg-control-accent"></div>
        </template>
    </div>
</template>

<script>
export const INITIAL_DISPLAY = {
    EQUAL: "equal", // All bars are equal height
    OSCILLATING: "oscillating", // Bars are oscillating
};

// determines bar height, not width
export const BAR_SIZE = {
    NORMAL: "normal",
    SMALL: "small",
    TINY: "tiny",
};
</script>
<script setup>
import { useElementSize } from "@vueuse/core";
import { computed, useTemplateRef } from "vue";

const props = defineProps({
    levels: {
        type: Array,
        required: true,
    },
    initialDisplay: {
        type: String,
        default: INITIAL_DISPLAY.EQUAL,
    },
    barSize: {
        type: String,
        default: BAR_SIZE.NORMAL,
    },
    // "bars" (default) renders the level as a primary-colored bar scaled by volume. "dots" renders the
    // refreshed-composer look: a grey dot at rest that grows into an ink line as the level rises, still
    // driven by the real audio levels.
    variant: {
        type: String,
        default: "bars",
    },
});

const BAR_WIDTH = 3;
const BAR_GAP = 6;
const voiceLevelContainer = useTemplateRef("voiceLevelContainer");
const { width } = useElementSize(voiceLevelContainer);

const levelsCount = computed(() => Math.ceil(width.value / (BAR_WIDTH + BAR_GAP)));

const initialHistory = computed(() => initializeLevelsHistory());

const levelHistorySlice = computed(() => {
    if (props.levels.length === 0) {
        // The dots variant rests as quiet dots so recording opens on a flat dotted line rather than the
        // oscillating bars; lines only appear once real audio levels arrive.
        if (props.variant === "dots") {
            return new Array(levelsCount.value).fill(0.25);
        }
        return initialHistory.value.slice(-levelsCount.value);
    }

    const count = levelsCount.value;
    const levels = props.levels;

    if (levels.length >= count) {
        // Enough real data to fill the display — skip initial history entirely
        const start = levels.length - count;
        const result = new Array(count);
        for (let i = 0; i < count; i++) {
            result[i] = normalizeLevel(levels[start + i]);
        }
        return result;
    }

    // Not enough real data yet — pad with silent bars
    const padCount = count - levels.length;
    const result = new Array(padCount).fill(0.25);
    for (let i = 0; i < levels.length; i++) {
        result.push(normalizeLevel(levels[i]));
    }
    return result;
});

const sizeStyle = computed(() => {
    switch (props.barSize) {
        case BAR_SIZE.TINY:
            return { container: "min-h-2", bar: "h-2" };

        case BAR_SIZE.SMALL:
            return { container: "min-h-4", bar: "h-4" };

        case BAR_SIZE.NORMAL:
        default:
            return { container: "min-h-8", bar: "h-8" };
    }
});

function initializeLevelsHistory() {
    const domain = [0.5, 0.75, 1];
    let upwards = true;
    let position = 0;

    switch (props.initialDisplay) {
        case INITIAL_DISPLAY.OSCILLATING:
            return Array(levelsCount.value)
                .fill(0)
                .map(() => {
                    const result = domain[position];
                    if (upwards) {
                        position++;
                        if (position >= domain.length - 1) {
                            upwards = false;
                        }
                    } else {
                        position--;
                        if (position <= 0) {
                            upwards = true;
                        }
                    }
                    return result;
                });

        case INITIAL_DISPLAY.EQUAL:
        default:
            return Array(levelsCount.value).fill(0.5);
    }
}

function normalizeLevel(level) {
    // Raw level from AudioRecorder is peak waveform deviation, 0-1.
    // Normal speech close to a mic is typically 0.03-0.15.
    const modified = level * 500;

    if (modified >= 40) {
        return 1;
    } else if (modified >= 15) {
        return 0.75;
    } else if (modified >= 4) {
        return 0.5;
    }

    return 0.25;
}

function dotBarClasses(level) {
    const heights = { 1: "h-5", 0.75: "h-3.5", 0.5: "h-2" };
    const height = heights[level] ?? "h-0.5";
    const color = level > 0.25 ? "bg-ink" : "bg-ink-muted";
    return ["w-0.5", height, color];
}

function levelHeight(level) {
    switch (level) {
        case 1:
            return "transform: scaleY(2)";
        case 0.75:
            return "transform: scaleY(1.5)";
        case 0.5:
            return "transform: scaleY(1)";
        default:
            return "transform: scaleY(0.5)";
    }
}
</script>
